"use client";

import { useState, useRef } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { CategoryTabs } from "@/components/pos/CategoryTabs";
import { MenuGrid } from "@/components/pos/MenuGrid";
import { ModifierModal } from "@/components/pos/ModifierModal";
import { PaymentModal } from "@/components/pos/PaymentModal";
import { CafeCustomizer } from "@/components/pos/CafeCustomizer";
import { useCategories, useMenuItems, useMenuItem } from "@/hooks/useMenu";
import { usePOS } from "@/hooks/usePOS";
import type { BusinessType } from "@/hooks/useBusinessType";
import { formatCurrency, cn } from "@/lib/utils";
import { toast } from "sonner";
import Link from "next/link";

interface QuickServicePOSProps {
  businessType: BusinessType;
}

export function QuickServicePOS({ businessType }: QuickServicePOSProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [lastOrderId, setLastOrderId] = useState<string | null>(null);
  const [lastOrderNumber, setLastOrderNumber] = useState<string>("");
  const [lastOrderTotal, setLastOrderTotal] = useState(0);
  const [barcodeSearch, setBarcodeSearch] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [showCafeCustomizer, setShowCafeCustomizer] = useState(false);
  const [cafeItem, setCafeItem] = useState<{ id: string; name: string; price: number } | null>(null);
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const { categories, loading: categoriesLoading } = useCategories();
  const { items, loading: itemsLoading } = useMenuItems({
    categoryId: selectedCategoryId,
    search: barcodeSearch || undefined,
  });
  const { item: selectedItemDetail } = useMenuItem(selectedItemId);

  const pos = usePOS();

  const isCafe = businessType === "CAFE";


  const handleItemClick = (item: { id: string; name: string; price: number; itemType?: string }) => {
    if (isCafe && item.itemType === "BEVERAGE") {
      setCafeItem(item);
      setShowCafeCustomizer(true);
      return;
    }
    setSelectedItemId(item.id);
  };

  const handleAddToOrder = (
    modifiers: Array<{ modifierId: string; name: string; price: number }>,
    quantity: number,
    notes: string,
  ) => {
    if (!selectedItemDetail) return;
    for (let i = 0; i < quantity; i++) {
      pos.addItem({
        menuItemId: selectedItemDetail.id,
        name: selectedItemDetail.name,
        price: selectedItemDetail.price,
        modifiers,
        notes,
      });
    }
    setSelectedItemId(null);
  };

  const handleQuickAdd = (item: { id: string; name: string; price: number }) => {
    pos.addItem({
      menuItemId: item.id,
      name: item.name,
      price: item.price,
    });
  };

  const handleCafeCustomizerConfirm = (
    modifiers: Array<{ modifierId: string; name: string; price: number }>,
    notes: string,
  ) => {
    if (!cafeItem) return;
    pos.addItem({
      menuItemId: cafeItem.id,
      name: cafeItem.name,
      price: cafeItem.price,
      modifiers,
      notes,
    });
    setShowCafeCustomizer(false);
    setCafeItem(null);
  };

  // Quick checkout: create order and go straight to payment (no kitchen step)
  const handleCharge = async () => {
    if (pos.items.length === 0) return;

    try {
      const createRes = await fetch("/api/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "TAKEOUT",
          guestCount: 1,
          items: pos.items.map((item) => ({
            menuItemId: item.menuItemId,
            quantity: item.quantity,
            notes: item.notes || undefined,
            modifiers: item.modifiers.map((m) => ({ modifierId: m.modifierId })),
          })),
        }),
      });

      if (!createRes.ok) {
        const err = await createRes.json();
        throw new Error(err.error || "Failed to create order");
      }

      const order = await createRes.json();
      const orderId = order.data?.id || order.id;
      const orderNum = order.data?.orderNumber || order.orderNumber || "";
      const orderTotal = order.data?.totalAmount || order.totalAmount || 0;

      setLastOrderId(orderId);
      setLastOrderNumber(orderNum);
      setLastOrderTotal(orderTotal);
      setShowPayment(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to process");
    }
  };

  const handlePaymentComplete = () => {
    setShowPayment(false);
    setLastOrderId(null);
    setLastOrderNumber("");
    setLastOrderTotal(0);
    pos.clearOrder();
    toast.success("Payment complete!");
  };

  const handleBarcodeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBarcodeSearch(e.target.value);
  };

  const handleBarcodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && barcodeSearch) {
      // Auto-add first matching item
      const match = items.find(
        (i) => i.isActive && (i.sku === barcodeSearch || i.barcode === barcodeSearch),
      );
      if (match) {
        handleQuickAdd({ id: match.id, name: match.name, price: match.price });
        setBarcodeSearch("");
        toast.success(`Added ${match.name}`);
      } else {
        toast.error("Product not found");
      }
    }
  };

  // Auto-add items without modifiers
  if (
    selectedItemId &&
    selectedItemDetail &&
    (!selectedItemDetail.modifierGroups || selectedItemDetail.modifierGroups.length === 0)
  ) {
    handleQuickAdd(selectedItemDetail);
    setSelectedItemId(null);
  }

  const showModifierModal =
    selectedItemId &&
    selectedItemDetail &&
    selectedItemDetail.modifierGroups &&
    selectedItemDetail.modifierGroups.length > 0;

  const itemCount = pos.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="flex flex-col h-screen bg-black">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-white/8 shrink-0">
        <div className="flex items-center gap-3">
          <Link href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white">
              <span className="text-xs font-bold text-black">NX</span>
            </div>
            <span className="text-sm font-semibold text-white">Facet</span>
          </Link>
          <Badge variant="info" size="sm">
            {isCafe ? "Cafe" : "Quick Service"}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs text-success">Live</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Menu area (fills most of the screen) */}
        <div className="flex flex-col flex-1 min-w-0">
          {/* Barcode / SKU search bar */}
          <div className="px-4 pt-3 pb-2 shrink-0">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-dim"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                />
              </svg>
              <input
                ref={barcodeInputRef}
                type="text"
                data-barcode-input="true"
                value={barcodeSearch}
                onChange={handleBarcodeInput}
                onKeyDown={handleBarcodeKeyDown}
                placeholder="Scan barcode or search by SKU..."
                className="w-full rounded-xl border border-surface-border bg-surface pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-text-dim focus:outline-none focus:ring-2 focus:ring-accent/30"
              />
              {barcodeSearch && (
                <button
                  onClick={() => setBarcodeSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-dim hover:text-white"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Category tabs */}
          <div className="px-4 pb-2 shrink-0">
            {categoriesLoading ? (
              <div className="flex items-center justify-center py-3">
                <Spinner size="sm" />
              </div>
            ) : (
              <CategoryTabs
                categories={categories.map((c) => ({
                  id: c.id,
                  name: c.name,
                  color: c.color || undefined,
                  itemCount: c._count.items,
                }))}
                selectedId={selectedCategoryId}
                onSelect={setSelectedCategoryId}
              />
            )}
          </div>

          {/* Menu grid */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <MenuGrid
              items={items
                .filter((i) => i.isActive)
                .map((i) => ({
                  id: i.id,
                  name: i.name,
                  price: i.price,
                  image: i.image || undefined,
                  isPopular: i.isPopular,
                  itemType: i.itemType,
                }))}
              onItemClick={handleItemClick}
              loading={itemsLoading}
            />
          </div>
        </div>

        {/* Right: Order summary drawer */}
        <div
          className={cn(
            "flex flex-col border-l border-white/5 bg-surface/50 transition-all duration-300",
            showDrawer ? "w-[340px] min-w-[340px]" : "w-[72px] min-w-[72px]",
          )}
        >
          {!showDrawer ? (
            // Collapsed sidebar
            <button
              onClick={() => setShowDrawer(true)}
              className="flex flex-col items-center justify-center h-full gap-2 hover:bg-white/5 transition-colors"
            >
              <div className="relative">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                </svg>
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[10px] font-bold text-black">
                    {itemCount}
                  </span>
                )}
              </div>
              <span className="text-lg font-bold text-white">{formatCurrency(pos.total)}</span>
            </button>
          ) : (
            // Expanded order panel
            <>
              <div className="flex items-center justify-between border-b border-white/8 px-4 py-3 shrink-0">
                <h2 className="text-base font-semibold text-white">Order</h2>
                <button
                  onClick={() => setShowDrawer(false)}
                  className="rounded-lg p-1.5 text-text-muted hover:bg-white/5 hover:text-white"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Items list */}
              <div className="flex-1 overflow-y-auto px-4">
                {pos.items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <svg className="h-10 w-10 text-surface-border-2 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                    </svg>
                    <p className="text-sm text-text-muted">No items yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5 py-2">
                    {pos.items.map((item, index) => (
                      <div key={index} className="py-2.5 flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{item.name}</p>
                          {item.modifiers.length > 0 && (
                            <p className="text-xs text-text-dim mt-0.5">
                              {item.modifiers.map((m) => m.name).join(", ")}
                            </p>
                          )}
                          <p className="text-xs text-text-muted mt-0.5">{formatCurrency(item.unitPrice)}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={() => pos.updateQuantity(index, item.quantity - 1)}
                            className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-white hover:bg-white/10"
                          >
                            -
                          </button>
                          <span className="text-sm font-medium text-white w-6 text-center">{item.quantity}</span>
                          <button
                            onClick={() => pos.updateQuantity(index, item.quantity + 1)}
                            className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-white hover:bg-white/10"
                          >
                            +
                          </button>
                        </div>
                        <span className="text-sm font-medium text-white w-16 text-right shrink-0">
                          {formatCurrency(item.totalPrice)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Totals + Charge */}
              <div className="border-t border-white/8 px-4 py-4 space-y-3 shrink-0">
                {pos.items.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-text-muted">Subtotal</span>
                      <span className="text-white/80">{formatCurrency(pos.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-muted">Tax</span>
                      <span className="text-white/80">{formatCurrency(pos.taxAmount)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/8">
                      <span className="text-white">Total</span>
                      <span className="text-white">{formatCurrency(pos.total)}</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={() => pos.clearOrder()}
                    disabled={pos.items.length === 0}
                    className="flex-1 text-danger hover:text-red-300 hover:bg-danger/10 disabled:text-text-dim"
                  >
                    Clear
                  </Button>
                  <Button
                    variant="primary"
                    size="xl"
                    onClick={handleCharge}
                    disabled={pos.items.length === 0}
                    className="flex-[2] bg-success hover:bg-emerald-600"
                  >
                    Charge {formatCurrency(pos.total)}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Running total bar at bottom (visible when drawer is collapsed) */}
      {!showDrawer && pos.items.length > 0 && (
        <div className="flex items-center justify-between border-t border-white/8 px-4 py-3 bg-surface/50 shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-sm text-text-muted">{itemCount} items</span>
            <span className="text-lg font-bold text-white">{formatCurrency(pos.total)}</span>
          </div>
          <Button
            variant="primary"
            size="lg"
            onClick={handleCharge}
            className="bg-success hover:bg-emerald-600"
          >
            Charge {formatCurrency(pos.total)}
          </Button>
        </div>
      )}

      {/* Modifier Modal */}
      {showModifierModal && selectedItemDetail && (
        <ModifierModal
          item={{
            name: selectedItemDetail.name,
            price: selectedItemDetail.price,
            modifierGroups: selectedItemDetail.modifierGroups || [],
          }}
          onConfirm={handleAddToOrder}
          onClose={() => setSelectedItemId(null)}
        />
      )}

      {/* Cafe Customizer */}
      {showCafeCustomizer && cafeItem && (
        <CafeCustomizer
          item={cafeItem}
          onConfirm={handleCafeCustomizerConfirm}
          onClose={() => {
            setShowCafeCustomizer(false);
            setCafeItem(null);
          }}
        />
      )}

      {/* Payment Modal */}
      {showPayment && lastOrderId && (
        <PaymentModal
          orderId={lastOrderId}
          orderNumber={lastOrderNumber}
          total={lastOrderTotal}
          onClose={() => setShowPayment(false)}
          onPaymentComplete={handlePaymentComplete}
        />
      )}
    </div>
  );
}
